import React, { useEffect, useMemo, useState } from "react";
import Card from "shared/Card";
import FormattedName from "shared/FormattedName";
import { useQueryParams } from "shared/@hooks/useQueryParams";
import { Section } from "shared/layout";
import Link from "shared/Link";
import Table from "shared/Table";
import TableSortHeader, { TableSortValue } from "shared/TableSortHeader";
import styles from "./index.module.css";
import SearchQueryBuilder from "./SearchQueryBuilder";
import PinLabel from "shared/PinLabel";
import PagingControls from "shared/PagingControls";
import LoadingContainer from "shared/LoadingContainer";
import { useAuthedRequest } from "shared/@auth/Auth";
import { useLogger } from "shared/@hooks/useLogger";

export default function LookupByPinLevel() {
  const { logError } = useLogger({ prefix: "LookupByPinLevel" });
  const query = useQueryParams();

  // Get all query params in one go.
  const q = query.get();

  // Parse search options from query params.
  const pins = useMemo(
    () =>
      (q.pins ?? "4")
        .split(",")
        .filter((p) => p !== "")
        .map(Number),
    [q.pins]
  );
  const filters = useMemo(
    () => (q.filters ?? "").split(",").filter((f) => f !== ""),
    [q.filters]
  );
  const sort = q.sort ?? "name-asc";

  // Define paging options.
  const page = Math.max(0, parseInt(q.page ?? "0", 10));
  const limit = parseInt(q.limit ?? "50", 10);

  // Regenerate searchParams when its properties change.
  const searchParams = useMemo(() => ({ pins, filters }), [pins, filters]);

  // Generate a fancy header based on pin names.
  const reportHeader = useMemo(() => {
    const parts = [];

    if (pins.includes(4)) {
      parts.push("Diamonds");
    }

    if (pins.includes(3)) {
      parts.push("Emeralds");
    }

    if (pins.includes(2)) {
      parts.push("Platinums");
    }

    if (parts.length > 0) {
      if (parts.length > 1) {
        const last = parts.pop();

        return parts.join(", ") + ` and ${last}`;
      } else {
        return parts.join(", ");
      }
    } else {
      return "No Pins Selected";
    }
  }, [pins]);

  // Make request with search params.
  const { data, loading, error } = useAuthedRequest(
    `/v1/membership/lookup/pin?pin=${pins.join(",")}&page=${
      page + 1 // TODO: Remove +1 when route is updated to use 0-indexed paging.
    }&limit=${limit}&filters=${filters}&sort=${sort}`
  );

  const [results, setResults] = useState(null);
  const [paging, setPaging] = useState(null);

  // TODO: Remove the -1 when this route is updated to use 0-indexed paging.
  const lastPage = paging && paging.last - 1;

  // Hold results until replaced. Table will show old results while loading.
  useEffect(() => {
    if (data != null) {
      setResults(data.data);
      setPaging(data.paging);
    }
  }, [data]);

  return (
    <div className={styles.container}>
      <SearchQueryBuilder
        params={searchParams}
        onChange={(values) => {
          query.set({
            ...values,
            sort,
            page,
            limit,
          });
        }}
      />

      <Card>
        <Section>
          <header>
            <h2>{reportHeader}</h2>
          </header>

          {pins.length === 0 ? (
            <span>Please select a pin checkbox above.</span>
          ) : (
            <LoadingContainer isLoading={loading || data == null}>
              <div className={styles.pagingControlsTop}>
                <PagingControls
                  page={page}
                  lastPage={lastPage}
                  limit={limit}
                  onPageChange={(value) => query.set({ page: value })}
                  onLimitChange={(value) => query.set({ limit: value })}
                  disableNext={paging?.last <= paging?.page}
                  disabled={loading}
                />
              </div>

              <Table>
                <thead>
                  <tr>
                    <th>
                      <TableSortHeader
                        value={
                          sort === "name-asc"
                            ? TableSortValue.ascending
                            : sort === "name-desc"
                            ? TableSortValue.descending
                            : TableSortValue.none
                        }
                        onChange={(value) => {
                          switch (value) {
                            case TableSortValue.ascending:
                              query.set({ sort: "name-asc" });
                              break;
                            case TableSortValue.descending:
                              query.set({ sort: "name-desc" });
                              break;
                            case TableSortValue.none:
                              query.set({ sort: null });
                              break;
                          }
                        }}
                      >
                        Name
                      </TableSortHeader>
                    </th>

                    <th>
                      <TableSortHeader
                        value={
                          sort === "ibo-asc"
                            ? TableSortValue.ascending
                            : sort === "ibo-desc"
                            ? TableSortValue.descending
                            : TableSortValue.none
                        }
                        onChange={(value) => {
                          switch (value) {
                            case TableSortValue.ascending:
                              query.set({ sort: "ibo-asc" });
                              break;
                            case TableSortValue.descending:
                              query.set({ sort: "ibo-desc" });
                              break;
                            case TableSortValue.none:
                              query.set({ sort: null });
                              break;
                          }
                        }}
                      >
                        IBO
                      </TableSortHeader>
                    </th>

                    <th>
                      <TableSortHeader
                        value={
                          sort === "pin-asc"
                            ? TableSortValue.ascending
                            : sort === "pin-desc"
                            ? TableSortValue.descending
                            : TableSortValue.none
                        }
                        onChange={(value) => {
                          switch (value) {
                            case TableSortValue.ascending:
                              query.set({ sort: "pin-asc" });
                              break;
                            case TableSortValue.descending:
                              query.set({ sort: "pin-desc" });
                              break;
                            case TableSortValue.none:
                              query.set({ sort: null });
                              break;
                          }
                        }}
                      >
                        Pin Level
                      </TableSortHeader>
                    </th>

                    <th>Status</th>
                    <th>Membership</th>
                    <th>Email</th>
                  </tr>
                </thead>

                <tbody>
                  {results != null &&
                    (results.length > 0 ? (
                      results.map((ibo) => (
                        <tr key={ibo.accountPK}>
                          <td>
                            <FormattedName value={ibo.fullName} />
                          </td>
                          <td>
                            <Link
                              to={`/membership/lookup/ibo/${ibo.iboNumber}`}
                            >
                              {ibo.iboNumber}
                            </Link>
                          </td>
                          <td>
                            <PinLabel pinLevel={ibo.pinLevel} />
                          </td>

                          <td>{ibo.status}</td>

                          <td>
                            {ibo.membership}
                            {ibo.hasDigitalDelivery && ibo.hasDreamStream
                              ? " +"
                              : ibo.hasDigitalDelivery
                              ? " + DD"
                              : ibo.hasDreamStream
                              ? " + Topics"
                              : ""}
                          </td>

                          <td>
                            <span className={styles.email}>{ibo.email}</span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6}>
                          <div>No results found.</div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>

              <div className={styles.pagingControls}>
                <PagingControls
                  page={page}
                  lastPage={lastPage}
                  limit={limit}
                  onPageChange={(value) => query.set({ page: value })}
                  onLimitChange={(value) => query.set({ limit: value })}
                  disableNext={paging?.last <= paging?.page}
                  disabled={loading}
                />
              </div>
            </LoadingContainer>
          )}
        </Section>
      </Card>
    </div>
  );
}
